import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Overlay from "ol/Overlay";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { getArea } from "ol/sphere";
import { unByKey } from "ol/Observable";
import Draw from "ol/interaction/Draw";
import "./AreaMeasurer.css";
import { source } from "../../_config/layers/vector";
import { vector } from "../../_config/layers/";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import iconArea from "../../_assets/img/areaMeasurer.png";

let drawArea, sketchArea;
let helpTooltipElementArea, helpTooltipArea;
let measureTooltipElementArea, measureTooltipArea;

function AreaMeasurer({ map, activeTool, handleActiveTool }) {
  const { t, i18n } = useTranslation();
  const htmlElement = useRef();
  const [active, setActive] = useState(false);

  /** -------- formatter -------- */
  const formatArea = (polygon) => {
    const area = getArea(polygon, { projection: "EPSG:4326" });
    return area > 10000
      ? `${Math.round((area / 1_000_000) * 100) / 100} ${t("areaMeasurer.km2")}`
      : `${Math.round(area * 100) / 100} ${t("areaMeasurer.m2")}`;
  };

  /** -------- help tooltip pointer -------- */
  const pointerMoveHandler = (evt) => {
    if (evt.dragging) return;

    helpTooltipElementArea.innerHTML = sketchArea
      ? t("areaMeasurer.continueMsg")
      : t("areaMeasurer.initMsg");

    helpTooltipArea.setPosition(evt.coordinate);
    helpTooltipElementArea.classList.remove("hidden");
  };

  /** -------- interactions -------- */
  const addInteraction = () => {
    drawArea = new Draw({
      source,
      type: "Polygon",
      style: new Style({
        fill: new Fill({ color: "rgba(255,255,255,0.2)" }),
        stroke: new Stroke({
          color: "rgba(0,0,0,0.5)",
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({ color: "rgba(0,0,0,0.7)" }),
          fill: new Fill({ color: "rgba(255,255,255,0.2)" }),
        }),
      }),
    });

    map.addInteraction(drawArea);
    createMeasureTooltip();
    createHelpTooltip();

    let listener;
    drawArea.on("drawstart", (evt) => {
      sketchArea = evt.feature;
      let tooltipCoord = evt.coordinate;

      listener = sketchArea.getGeometry().on("change", (evt) => {
        const geom = evt.target;
        measureTooltipElementArea.innerHTML = formatArea(geom);
        tooltipCoord = geom.getInteriorPoint().getCoordinates();
        measureTooltipArea.setPosition(tooltipCoord);
      });
    });

    drawArea.on("drawend", () => {
      measureTooltipElementArea.className = "ol-tooltip ol-tooltip-static";
      measureTooltipArea.setOffset([0, -7]);
      sketchArea = null;
      measureTooltipElementArea = null;
      createMeasureTooltip();
      unByKey(listener);
    });
  };

  /** -------- tooltip helpers -------- */
  const createHelpTooltip = () => {
    if (helpTooltipElementArea) helpTooltipElementArea.remove();
    helpTooltipElementArea = document.createElement("div");
    helpTooltipElementArea.className = "ol-tooltip hidden";
    helpTooltipArea = new Overlay({
      element: helpTooltipElementArea,
      offset: [15, 0],
      positioning: "center-left",
    });
    map.addOverlay(helpTooltipArea);
  };

  const createMeasureTooltip = () => {
    measureTooltipElementArea = document.createElement("div");
    measureTooltipElementArea.className = "ol-tooltip ol-tooltip-measure";
    measureTooltipArea = new Overlay({
      element: measureTooltipElementArea,
      offset: [0, -15],
      positioning: "bottom-center",
      stopEvent: false,
      insertFirst: false,
    });
    map.addOverlay(measureTooltipArea);
  };

  /** -------- enable / disable -------- */
  const handleActivity = (activate) => {
    if (activate) {
      map.on("pointermove", pointerMoveHandler);
      map.getViewport().addEventListener("mouseout", () => {
        helpTooltipElementArea.classList.add("hidden");
      });
      addInteraction();
    } else {
      map.removeInteraction(drawArea);
      if (helpTooltipElementArea) helpTooltipElementArea.style.display = "none";
      source.clear();
      document
        .querySelectorAll(".ol-tooltip-static")
        .forEach((el) => el.remove());
    }
    setActive(activate);
  };

  /** -------- effects -------- */
  useEffect(() => handleActivity(active), [active]);
  useEffect(() => {
    if (activeTool !== htmlElement) handleActivity(false);
  }, [activeTool]);

  /** -------- button click -------- */
  const handleClick = () => {
    setActive(!active);
    handleActiveTool(htmlElement);
    vector.getSource().clear();
  };

  /** -------- render -------- */
  return (
    <>
      <Button
        title={t("areaMeasurer.buttonTooltip")}
        alt={t("areaMeasurer.buttonTooltip")}
        onClick={handleClick}
        ref={htmlElement}
        className={`AreaMeasurerButton AreaMeasurer ${
          active ? "AreaMeasurerActive" : "AreaMeasurerDeactive"
        }`}
        severity="info"
        rounded
      >
      <img src={iconArea} alt="area" style={{ width: 30, height: 20 }} />
      </Button>

      <Tooltip
        target=".AreaMeasurerButton"
        content={t("areaMeasurer.buttonTooltip")}
        position="bottom"
        className="CustomTooltip"
      />
    </>
  );
}

export default AreaMeasurer;
